// Copyright (c) 2026 WiseTime. All rights reserved.

import { Observable, of, throwError } from "rxjs"
import { Action } from "redux"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import * as Actions from "../actions"
import { setWelcomeVideoState, WelcomeVideoState } from "../welcome-video/slice"
import { WELCOME_VIDEO_STATE_KEY, CUSTOMER_USER_SIGNUP_TS_KEY } from "../../../app/localStorageKeys"
import { WELCOME_VIDEO_WINDOW_DAYS } from "../welcome-video/types"

export type StorageReader = () => Observable<Partial<WelcomeVideoState> | undefined>

export const load: StorageReader = () => {
  try {
    // First, check if there's a signup timestamp from customer-frontend
    const signupTsStr = localStorage.getItem(CUSTOMER_USER_SIGNUP_TS_KEY)

    if (signupTsStr) {
      const signupTs = parseInt(signupTsStr, 10)

      if (!isNaN(signupTs)) {
        const now = Math.floor(Date.now() / 1000)
        const daysSinceSignup = (now - signupTs) / (24 * 60 * 60)

        // If past 30 days since signup, clean up localStorage and don't initialize
        if (daysSinceSignup > WELCOME_VIDEO_WINDOW_DAYS) {
          localStorage.removeItem(WELCOME_VIDEO_STATE_KEY)
          localStorage.removeItem(CUSTOMER_USER_SIGNUP_TS_KEY)
          return of(undefined)
        }

        // Within 30 days - check if we have existing state
        const existingData = localStorage.getItem(WELCOME_VIDEO_STATE_KEY)
        if (existingData !== null) {
          const parsed = JSON.parse(existingData)
          return of(parsed)
        }

        // No existing state but within 30 days - initialize as new user
        return of({ dismissed: false })
      }
    }

    // No signup timestamp - could be old user, don't initialize
    return of(undefined)
  } catch (error) {
    return throwError(error)
  }
}

export const loadWelcomeVideoState = (load: StorageReader) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.loadWelcomeVideoStateFromStorage.match),
      mergeMap(_ => load().pipe(
        map(state => {
          // Only set state if localStorage had data (new users)
          // Otherwise keep initial state (existing users)
          if (state) {
            return setWelcomeVideoState(state)
          }
          // Return a no-op action for existing users
          return Actions.loadWelcomeVideoStateFromStorageFailed()
        }),
        catchError(_ => of(Actions.loadWelcomeVideoStateFromStorageFailed())),
      )),
    )

export default loadWelcomeVideoState(load)
